import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Typography,
  TextField,
  MenuItem,
  Button,
  Stack,
  InputAdornment,
  Alert,
  alpha,
  CircularProgress,
} from '@mui/material';
import {
  AttachMoney,
  ContentCopy,
  Check,
} from '@mui/icons-material';
import { GlassCard } from '../../components/v2';
import { gradients } from '../../theme/v2Theme';
import Coins from '../../assets/coins/coins.json';
import WAV from 'multicoin-address-validator';
import toast from 'react-hot-toast';

const coinGradients: { [key: string]: string } = {
  BTC: gradients.sunset,
  ETH: gradients.ocean,
  USDC: gradients.success,
  USDT: gradients.success,
  MATIC: gradients.lavender,
  SOL: gradients.primary,
  BNB: gradients.sunset,
};

export const PayPage: React.FC = () => {
  const [formData, setFormData] = useState({
    coin: 'ETH',
    address: '',
    amount: '',
  });
  const [generatedLink, setGeneratedLink] = useState('');
  const [copied, setCopied] = useState(false);
  const [addressError, setAddressError] = useState('');
  const [validatedCoins, setValidatedCoins] = useState<string[]>([]);
  const [isValidating, setIsValidating] = useState(false);

  const coins = Coins as { [key: string]: any };

  // Generate crypto list from all coins in Coins.json
  const allCryptos = Object.keys(coins).map((coinKey) => {
    const coinData = coins[coinKey];
    return {
      value: coinKey,
      label: `${coinData.name || coinKey} (${coinKey})`,
      icon: coinData.symbol || coinKey.charAt(0),
    };
  }).sort((a, b) => {
    const priority = ['BTC', 'ETH', 'USDC', 'USDT'];
    const aIndex = priority.indexOf(a.value);
    const bIndex = priority.indexOf(b.value);

    if (aIndex !== -1 && bIndex !== -1) {
      return aIndex - bIndex;
    }
    if (aIndex !== -1) return -1;
    if (bIndex !== -1) return 1;

    return a.label.localeCompare(b.label);
  });

  // Validate address and detect compatible coins
  useEffect(() => {
    if (!formData.address || formData.address.trim().length === 0) {
      setValidatedCoins([]);
      setAddressError('');
      setIsValidating(false);
      return;
    }

    setIsValidating(true);
    const address = formData.address.trim();
    const validCoins: string[] = [];

    // Check all coins to see which ones this address is valid for
    Object.keys(coins).forEach((coinKey) => {
      const coinInfo = coins[coinKey];
      try {
        let isValid = false;
        if (coinInfo.ERC20) {
          isValid = WAV.validate(address, 'ETH');
        } else {
          isValid = WAV.validate(address, coinKey);
        }

        if (isValid) {
          validCoins.push(coinKey);
        }
      } catch (error) {
        // Validation error, skip this coin
      }
    });

    setValidatedCoins(validCoins);

    if (validCoins.length === 0) {
      setAddressError('Invalid address format. Please check and try again.');
    } else {
      setAddressError('');
      if (!validCoins.includes(formData.coin)) {
        setFormData((prev) => ({ ...prev, coin: validCoins[0] }));
      }
    }
    setIsValidating(false);
  }, [formData.address, coins, formData.coin]);

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    setGeneratedLink('');
  };

  const handleGenerate = () => {
    if (!formData.address) {
      return;
    }
    const baseUrl = window.location.origin;
    const link = formData.amount
      ? `${baseUrl}/pay/${formData.coin}/${formData.address}?amount=${formData.amount}`
      : `${baseUrl}/pay/${formData.coin}/${formData.address}`;
    setGeneratedLink(link);
    toast.success('Payment link generated!');
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(generatedLink);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  // Post dynamic height to parent when inside an iframe
  useEffect(() => {
    try {
      if (window.self === window.top) return;
    } catch {}
    const postSize = () => {
      const height = document.body.scrollHeight;
      try {
        window.parent.postMessage({ type: 'addrs-embed:height', height }, '*');
      } catch {}
    };
    postSize();
    const ro = new ResizeObserver(() => postSize());
    ro.observe(document.body);
    const i = setInterval(postSize, 1000);
    return () => {
      try { ro.disconnect(); } catch {}
      clearInterval(i);
    };
  }, []);

  return (
    <Box sx={{ background: '#ffffff00', minHeight: '100vh', display: 'flex', alignItems: 'center', py: 2 }}>
      <Container maxWidth="sm" style={{textAlign: 'center'}}>
        <Box sx={{ background: '#fff', borderRadius: 3, overflow: 'hidden', boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}>
          {/* Header */}
          <Box sx={{
            background: gradients.primary,
            p: 2.5,
            textAlign: 'center',
            position: 'relative',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 100%)',
            }
          }}>
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <AttachMoney sx={{ fontSize: 48, color: '#fff', mb: 0.75 }} />
              <Typography variant="h5" fontWeight={800} sx={{ color: '#fff', mb: 0.5 }}>
                Create Payment Link
              </Typography>
              <Typography variant="body2" sx={{ color: alpha('#fff', 0.9), fontSize: '0.875rem' }}>
                Generate a custom crypto payment link
              </Typography>
            </Box>
          </Box>

          {/* Content */}
          <Box sx={{ p: 2.5 }}>
            {!generatedLink ? (
              <Stack spacing={2}>
                {/* Cryptocurrency Select */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
                    Cryptocurrency
                  </Typography>
                  <TextField
                    fullWidth
                    select
                    value={formData.coin}
                    onChange={(e) => handleChange('coin', e.target.value)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha('#000', 0.2),
                          }
                        },
                      },
                    }}
                  >
                    {allCryptos.map((crypto) => (
                      <MenuItem key={crypto.value} value={crypto.value}>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            sx={{
                              width: 32,
                              height: 32,
                              borderRadius: 1.5,
                              background: coinGradients[crypto.value] || gradients.primary,
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontSize: '1rem',
                              fontWeight: 700,
                              color: '#fff',
                            }}
                          >
                            {crypto.icon}
                          </Box>
                          <Typography fontWeight={600}>{crypto.label}</Typography>
                        </Stack>
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>

                {/* Address Input */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
                    Your Wallet Address
                  </Typography>
                  <TextField
                    fullWidth
                    placeholder={`Enter your ${formData.coin} address`}
                    value={formData.address}
                    onChange={(e) => handleChange('address', e.target.value)}
                    multiline
                    rows={2}
                    error={!!addressError}
                    InputProps={{
                      sx: {
                        fontFamily: 'monospace',
                        fontSize: '0.875rem',
                        borderRadius: 2,
                      },
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha('#000', 0.2),
                          }
                        },
                      },
                    }}
                  />
                  {isValidating && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
                      <CircularProgress size={16} />
                      <Typography variant="caption" color="text.secondary">
                        Validating address...
                      </Typography>
                    </Box>
                  )}
                  {addressError && !isValidating && (
                    <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                      {addressError}
                    </Alert>
                  )}
                  {validatedCoins.length > 0 && !addressError && !isValidating && (
                    <Alert severity="success" sx={{ mt: 2, borderRadius: 2 }}>
                      Valid address! Compatible with {validatedCoins.length} token{validatedCoins.length > 1 ? 's' : ''}
                    </Alert>
                  )}
                </Box>

                {/* Amount Input */}
                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
                    Amount (Optional)
                  </Typography>
                  <TextField
                    fullWidth
                    type="number"
                    placeholder="0.00"
                    value={formData.amount}
                    onChange={(e) => handleChange('amount', e.target.value)}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <Typography fontWeight={700} color="text.secondary">
                            {formData.coin}
                          </Typography>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        borderRadius: 2,
                        '&:hover': {
                          '& .MuiOutlinedInput-notchedOutline': {
                            borderColor: alpha('#000', 0.2),
                          }
                        },
                      },
                    }}
                  />
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.75, display: 'block', fontSize: '0.75rem' }}>
                    Leave empty to let the payer choose
                  </Typography>
                </Box>

                {/* Generate Button */}
                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  disabled={!formData.address || !!addressError}
                  onClick={handleGenerate}
                  sx={{
                    background: gradients.primary,
                    py: 1.25,
                    borderRadius: 2,
                    fontWeight: 700,
                    fontSize: '0.95rem',
                    textTransform: 'none',
                    boxShadow: '0 2px 12px rgba(99, 102, 241, 0.25)',
                    transition: 'all 0.2s ease',
                    '&:hover': {
                      background: gradients.primary,
                      opacity: 0.9,
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 16px rgba(99, 102, 241, 0.35)',
                    },
                    '&:disabled': {
                      background: alpha('#cccccc', 0.5),
                    }
                  }}
                >
                  Generate Payment Link
                </Button>
              </Stack>
            ) : (
              <Stack spacing={2}>
                <Box textAlign="center">
                  <Check
                    sx={{
                      fontSize: 52,
                      color: 'success.main',
                      background: alpha('#10b981', 0.1),
                      borderRadius: '50%',
                      p: 1.5,
                      mb: 1.5,
                    }}
                  />
                  <Typography variant="h6" fontWeight={700} gutterBottom color="success.main">
                    Payment Link Generated!
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.875rem' }}>
                    Share this link to receive {formData.coin} payments
                  </Typography>
                </Box>

                <Box>
                  <Typography variant="subtitle2" fontWeight={700} color="text.secondary" sx={{ mb: 1 }}>
                    Your Payment Link
                  </Typography>
                  <Box
                    sx={{
                      p: 2,
                      background: alpha('#000', 0.05),
                      borderRadius: 2,
                      wordBreak: 'break-all',
                      fontSize: '0.875rem',
                      fontFamily: 'monospace',
                      border: '1px solid',
                      borderColor: alpha('#000', 0.08),
                    }}
                  >
                    {generatedLink}
                  </Box>
                </Box>

                <Stack spacing={1.25}>
                  <Button
                    variant="contained"
                    fullWidth
                    size="large"
                    startIcon={copied ? <Check /> : <ContentCopy />}
                    onClick={handleCopy}
                    sx={{
                      background: copied ? gradients.success : gradients.primary,
                      py: 1.25,
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      textTransform: 'none',
                      boxShadow: '0 2px 12px rgba(99, 102, 241, 0.25)',
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        background: copied ? gradients.success : gradients.primary,
                        opacity: 0.9,
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 16px rgba(99, 102, 241, 0.35)',
                      },
                    }}
                  >
                    {copied ? 'Copied!' : 'Copy Link'}
                  </Button>
                  <Button
                    variant="outlined"
                    fullWidth
                    size="large"
                    href={generatedLink}
                    target="_blank"
                    sx={{
                      py: 1.25,
                      borderRadius: 2,
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      textTransform: 'none',
                      borderWidth: 2,
                      transition: 'all 0.2s ease',
                      '&:hover': {
                        borderWidth: 2,
                        transform: 'translateY(-1px)',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      }
                    }}
                  >
                    Open Payment Page
                  </Button>
                  <Button
                    variant="text"
                    fullWidth
                    onClick={() => {
                      setGeneratedLink('');
                      setFormData({ coin: 'ETH', address: '', amount: '' });
                    }}
                    sx={{
                      py: 1.25,
                      fontWeight: 700,
                      fontSize: '0.95rem',
                      textTransform: 'none',
                    }}
                  >
                    Create Another Link
                  </Button>
                </Stack>

                <Alert severity="info" sx={{ borderRadius: 2 }}>
                  <Typography variant="body2">
                    <strong>Tip:</strong> Save this link to receive {formData.coin} payments from anyone
                  </Typography>
                </Alert>
              </Stack>
            )}
          </Box>
        </Box>
      </Container>
    </Box>
  );
};
